import React, { useMemo, useRef, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AppsIcon from "@mui/icons-material/Apps";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ConfirmDialog } from "../../common/dashboard/ConfirmDialog";
import {
  EmailFooterProfile,
  renderEmailBuilderState,
} from "./emailBuilderData";
import { EmailFormState, getEmailTypeLabel } from "./emailMockData";
import {
  confirmPreparedNewsletterTestSend,
  NewsletterTestSendPreview,
  PendingNewsletterTestSend,
  prepareNewsletterTestSendConfirmation,
} from "./emailTestSend";
import {
  AudienceSummaryRow,
  EmailFormMode,
  Field,
  MetricCard,
  UpdateEmailField,
  attachmentAccept,
  checkboxClassName,
  fieldLabelClassName,
  formatNumber,
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  sectionClassName,
  stepDescriptionClassName,
  stepTitleClassName,
  successButtonClassName,
  textareaClassName,
} from "./emailWizardShared";

export type StepFourReviewProps = {
  mode: EmailFormMode;
  email: EmailFormState;
  isBusy: boolean;
  isSentRecord: boolean;
  canRunSavedBackendAction: boolean;
  intendedRecipientCount: number | null;
  directRecipientCount: number;
  audienceRows: AudienceSummaryRow[];
  goToCampaignDetails: () => void;
  updateEmailField: UpdateEmailField;
  setNewsroomToggle: (checked: boolean) => void;
  addAttachment: (file?: File) => Promise<void>;
  onDuplicateAsDraft?: (email: EmailFormState) => Promise<void>;
  previewTestSend: (
    recipientEmails: string[],
    clientRequestId: string
  ) => Promise<NewsletterTestSendPreview>;
  sendReview: (
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => Promise<void>;
  scheduleEmail: () => Promise<void>;
  cancelSchedule: () => Promise<void>;
  publishNewsroom: () => Promise<void>;
  unpublishNewsroom: () => Promise<void>;
  sendNow: () => Promise<void>;
  hasSendReviewAction: boolean;
  hasScheduleAction: boolean;
  hasCancelScheduleAction: boolean;
  hasPublishNewsroomAction: boolean;
  hasUnpublishNewsroomAction: boolean;
  hasSendNowAction: boolean;
  footerProfile?: EmailFooterProfile | null;
};

const cardHeadingClassName = "text-[22px] font-extrabold leading-tight text-gray-950";

const StepFourReview = ({
  mode,
  email,
  isBusy,
  isSentRecord,
  canRunSavedBackendAction,
  intendedRecipientCount,
  directRecipientCount,
  audienceRows,
  goToCampaignDetails,
  updateEmailField,
  setNewsroomToggle,
  addAttachment,
  onDuplicateAsDraft,
  previewTestSend,
  sendReview,
  scheduleEmail,
  cancelSchedule,
  publishNewsroom,
  unpublishNewsroom,
  sendNow,
  hasSendReviewAction,
  hasScheduleAction,
  hasCancelScheduleAction,
  hasPublishNewsroomAction,
  hasUnpublishNewsroomAction,
  hasSendNowAction,
  footerProfile,
}: StepFourReviewProps) => {
  const intendedRecipientCountLabel =
    intendedRecipientCount === null
      ? "—"
      : formatNumber(intendedRecipientCount);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [testEmailInput, setTestEmailInput] = useState("");
  const [testEmailError, setTestEmailError] = useState("");
  const [pendingTestSend, setPendingTestSend] =
    useState<PendingNewsletterTestSend | null>(null);

  // The preview thumbnail and the full preview must both render THIS
  // campaign's real built content so they always agree with Email Summary.
  const renderedEmail = useMemo(
    () => renderEmailBuilderState(email.builderState, footerProfile || undefined),
    [email.builderState, footerProfile]
  );

  const openFullPreview = () => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) return;
    previewWindow.document.write(renderedEmail.html);
    previewWindow.document.close();
  };

  const handleSendTestEmail = async () => {
    try {
      setTestEmailError("");
      setPendingTestSend(
        await prepareNewsletterTestSendConfirmation({
          rawRecipientInput: testEmailInput,
          previewTestSend,
        })
      );
    } catch (error) {
      setTestEmailError(
        error instanceof Error
          ? error.message
          : "The test email could not be prepared."
      );
    }
  };

  const confirmSendTestEmail = async () => {
    if (!pendingTestSend) return;

    setTestEmailError("");
    const attempt = await confirmPreparedNewsletterTestSend({
      pending: pendingTestSend,
      sendTestEmail: sendReview,
    });
    if (attempt.sent) {
      setPendingTestSend(null);
    } else {
      setPendingTestSend(attempt.pending);
      setTestEmailError(attempt.error);
    }
  };

  const summaryRows: Array<{
    icon: React.ReactNode;
    label: string;
    value: string;
    emphasize?: boolean;
  }> = [
    {
      icon: <MailOutlineIcon fontSize="small" />,
      label: "Email Name",
      value: email.category || "—",
    },
    {
      icon: <DescriptionOutlinedIcon fontSize="small" />,
      label: "Subject Line",
      value: email.subject || "—",
    },
    {
      icon: <PersonOutlineIcon fontSize="small" />,
      label: "Sender Name",
      value: email.fromName || "—",
    },
    {
      icon: <AccessTimeIcon fontSize="small" />,
      label: "Reply-To Email",
      value: email.replyToEmail || email.fromEmail || "—",
    },
    {
      icon: <AppsIcon fontSize="small" />,
      label: "Email Type",
      value: getEmailTypeLabel(email.emailType).toUpperCase(),
      emphasize: true,
    },
    {
      icon: <DescriptionOutlinedIcon fontSize="small" />,
      label: "Content Type",
      value: "HTML Email",
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className={stepTitleClassName}>Review and Send</h2>
        <p className={stepDescriptionClassName}>
          Review your email campaign details before sending to your audience.
        </p>
      </div>

      {/* Email Summary */}
      <section className={sectionClassName}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-500">
              <MailOutlineIcon />
            </span>
            <h3 className={cardHeadingClassName}>Email Summary</h3>
          </div>
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={goToCampaignDetails}
            disabled={isBusy}
          >
            <EditOutlinedIcon fontSize="small" />
            Edit Campaign Details
          </button>
        </div>
        <dl className="mt-7 space-y-5">
          {summaryRows.map((row) => (
            <div
              key={row.label}
              className="flex flex-col gap-1 sm:flex-row sm:items-center"
            >
              <dt className="flex w-full shrink-0 items-center gap-3 text-[15px] text-gray-500 sm:w-[240px]">
                <span className="text-gray-400">{row.icon}</span>
                {row.label}
              </dt>
              <dd
                className={`min-w-0 break-words text-[16px] text-gray-950 ${
                  row.emphasize ? "font-extrabold" : "font-semibold"
                }`}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Email Preview */}
      <section className={sectionClassName}>
        <div className="flex items-center gap-3">
          <VisibilityOutlinedIcon className="text-gray-500" />
          <h3 className={cardHeadingClassName}>Email Preview</h3>
        </div>
        <div className="mt-7 flex justify-center">
          <div className="h-[380px] w-full max-w-[560px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <iframe
              srcDoc={renderedEmail.html}
              title="Email preview"
              scrolling="no"
              className="pointer-events-none h-[900px] w-full border-0"
            />
          </div>
        </div>
        <div className="mt-6 text-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-[16px] font-extrabold text-gray-950 underline-offset-2 hover:underline"
            onClick={openFullPreview}
          >
            Preview Full Email
            <OpenInNewIcon fontSize="small" />
          </button>
          <p className="mt-2 text-[14px] text-gray-400">
            See how your email will look to recipients.
          </p>
        </div>
      </section>

      {/* Audience Summary */}
      <section className={sectionClassName}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className={cardHeadingClassName}>Audience Summary</h3>
            <p className="mt-2 text-[15px] leading-6 text-gray-500">
              Your email will be sent to the following audience.
            </p>
          </div>
          <div className="rounded-lg bg-slate-100 px-5 py-3 text-right">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-gray-500">
              Total Recipients
            </p>
            <p className="mt-1 text-[26px] font-extrabold leading-none text-gray-950">
              {intendedRecipientCountLabel}
            </p>
          </div>
        </div>
        <p className="mt-7 text-[13px] font-extrabold uppercase tracking-wide text-gray-950">
          Selected Audiences
        </p>
        <div className="mt-3 space-y-3">
          {audienceRows.length === 0 ? (
            <p className="rounded-md border border-dashed border-gray-300 p-4 text-[15px] text-gray-500">
              No audience selected yet. Go back to Step 1 to choose your
              audience.
            </p>
          ) : (
            audienceRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3"
              >
                <span className="min-w-0 break-words text-[15px] text-gray-950">
                  {row.label}
                </span>
                <span className="shrink-0 text-[15px] font-extrabold text-gray-950">
                  {row.count === null ? "—" : formatNumber(row.count)}
                </span>
              </div>
            ))
          )}
        </div>
        <p className="mt-7 text-[13px] font-extrabold uppercase tracking-wide text-gray-950">
          Additional Recipients
        </p>
        <div className="mt-3 flex items-center justify-between gap-4 rounded-md border border-gray-200 px-4 py-3">
          <span className="text-[15px] text-gray-500">
            Manually added email addresses
          </span>
          <span className="shrink-0 text-[15px] font-extrabold text-gray-950">
            {formatNumber(directRecipientCount)}
          </span>
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-md border border-amber-400 bg-amber-50/50 px-5 py-4">
          <InfoOutlinedIcon className="mt-0.5 text-amber-500" fontSize="small" />
          <div className="text-[15px] leading-6">
            <p className="font-extrabold text-gray-950">
              {intendedRecipientCount === null
                ? "The latest recipient count is still being resolved."
                : `This email will be sent to ${intendedRecipientCountLabel} unique recipients.`}
            </p>
            <p className="text-gray-500">
              Duplicate email addresses have been removed.
            </p>
          </div>
        </div>
      </section>

      {/* Schedule (kept workflow capability) */}
      <section className={sectionClassName}>
        <h3 className={cardHeadingClassName}>Schedule (Optional)</h3>
        <p className="mt-2 text-[15px] leading-6 text-gray-500">
          Choose a date and time if you want to schedule this email instead of
          sending it right away.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Field label="Scheduled Date">
            <input
              type="date"
              value={email.scheduledDate}
              onChange={(event) =>
                updateEmailField("scheduledDate", event.target.value)
              }
              className={inputClassName}
              disabled={isBusy || isSentRecord}
            />
          </Field>
          <Field label="Scheduled Time">
            <input
              type="time"
              value={email.scheduledTime}
              onChange={(event) =>
                updateEmailField("scheduledTime", event.target.value)
              }
              className={inputClassName}
              disabled={isBusy || isSentRecord}
            />
          </Field>
          <div className="lg:col-span-2">
            <Field label="Internal Review Note">
              <textarea
                value={email.reviewNote}
                onChange={(event) =>
                  updateEmailField("reviewNote", event.target.value)
                }
                className={textareaClassName}
                disabled={isBusy}
                placeholder="Add a note for review, schedule, or send history."
              />
            </Field>
          </div>
        </div>
      </section>

      {/* Attachments (kept workflow capability) */}
      <section className={sectionClassName}>
        <h3 className={cardHeadingClassName}>Attachments</h3>
        <p className="mt-2 text-sm leading-5 text-gray-600">
          Images are converted and optimized automatically. Keep combined
          attachments at 6MB or less so the email can be sent reliably.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={attachmentAccept}
          className="hidden"
          onChange={(event) => {
            addAttachment(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <button
          type="button"
          className={`${secondaryButtonClassName} mt-5`}
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy || isSentRecord}
        >
          <FileUploadOutlinedIcon />
          Upload Attachment
        </button>
        <div className="mt-5 grid grid-cols-1 gap-3">
          {email.attachments.length === 0 ? (
            <p className="rounded-md border border-dashed border-gray-300 p-5 text-gray-500">
              No attachments added.
            </p>
          ) : (
            email.attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between rounded-md border border-gray-200 p-4"
              >
                <span>
                  <span className="block font-extrabold text-gray-950">
                    {attachment.name}
                  </span>
                  <span className="text-sm text-gray-500">{attachment.sizeLabel}</span>
                </span>
                <button
                  type="button"
                  className="text-sm font-extrabold text-red-600"
                  onClick={() =>
                    updateEmailField(
                      "attachments",
                      email.attachments.filter(
                        (currentAttachment) => currentAttachment.id !== attachment.id
                      )
                    )
                  }
                  disabled={isBusy || isSentRecord}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Newsroom (kept workflow capability) */}
      <section className={sectionClassName}>
        <h3 className={cardHeadingClassName}>Newsroom</h3>
        <label className="mt-5 flex items-center gap-3 text-[17px] font-extrabold text-gray-950">
          <input
            type="checkbox"
            checked={email.publishToNewsroom}
            onChange={(event) => setNewsroomToggle(event.target.checked)}
            className={checkboxClassName}
            disabled={isBusy}
          />
          Publish this sent email to the newsroom
        </label>
        {email.publishToNewsroom ? (
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Field label="Newsroom Slug">
              <input
                value={email.newsroomSlug}
                onChange={(event) =>
                  updateEmailField("newsroomSlug", event.target.value)
                }
                className={inputClassName}
                disabled={isBusy}
              />
            </Field>
            <Field label="SEO Title">
              <input
                value={email.seoTitle}
                onChange={(event) => updateEmailField("seoTitle", event.target.value)}
                className={inputClassName}
                disabled={isBusy}
              />
            </Field>
          </div>
        ) : null}
      </section>

      {/* Delivery Tracking (update mode only) */}
      {mode === "update" ? (
        <section className={sectionClassName}>
          <h3 className={cardHeadingClassName}>Delivery Tracking</h3>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <MetricCard
              label="Sent"
              value={formatNumber(email.sentRecipientCount)}
            />
            <MetricCard
              label="Failed"
              value={formatNumber(email.failedRecipientCount)}
            />
            <MetricCard label="Opens" value={formatNumber(email.openCount)} />
            <MetricCard label="Clicks" value={formatNumber(email.clickCount)} />
          </div>
          <div className="mt-6 overflow-x-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-left">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Kind",
                    "Status",
                    "Recipient",
                    "Intended Recipient",
                    "Created",
                    "Error",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-4 py-3 text-sm font-extrabold text-gray-700"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {email.outboundEmails.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-5 text-sm text-gray-500">
                      No outbound delivery rows yet.
                    </td>
                  </tr>
                ) : (
                  email.outboundEmails.slice(0, 8).map((outboundEmail) => (
                    <tr key={outboundEmail.id}>
                      <td className="px-4 py-3 text-sm font-semibold">
                        {outboundEmail.kind}
                      </td>
                      <td className="px-4 py-3 text-sm">{outboundEmail.status}</td>
                      <td className="px-4 py-3 text-sm">{outboundEmail.toEmail}</td>
                      <td className="px-4 py-3 text-sm">
                        {outboundEmail.intendedToEmail || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">{outboundEmail.createdAt}</td>
                      <td className="px-4 py-3 text-sm text-red-600">
                        {outboundEmail.errorMessage}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* Secondary saved-record actions (kept workflow capabilities) */}
      <div className="flex flex-wrap justify-end gap-4">
        {onDuplicateAsDraft ? (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={() => onDuplicateAsDraft(email)}
            disabled={!canRunSavedBackendAction || isBusy}
            title="Attachments are not copied; add them again on the new draft."
          >
            <ContentCopyOutlinedIcon />
            Duplicate without attachments
          </button>
        ) : null}
        <button
          type="button"
          className={secondaryButtonClassName}
          onClick={scheduleEmail}
          disabled={!canRunSavedBackendAction || !hasScheduleAction || isBusy}
        >
          <CalendarMonthIcon />
          Schedule Email
        </button>
        {email.status === "scheduled" ? (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={cancelSchedule}
            disabled={!canRunSavedBackendAction || !hasCancelScheduleAction || isBusy}
          >
            Cancel Schedule
          </button>
        ) : null}
        {email.newsroomStatus === "published" ? (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={unpublishNewsroom}
            disabled={!canRunSavedBackendAction || !hasUnpublishNewsroomAction || isBusy}
          >
            <UnpublishedOutlinedIcon />
            Unpublish Newsroom
          </button>
        ) : (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={publishNewsroom}
            disabled={!canRunSavedBackendAction || !hasPublishNewsroomAction || isBusy}
          >
            <PublishOutlinedIcon />
            Publish Newsroom
          </button>
        )}
      </div>

      {/* Send Test Email + Send Email */}
      <section className={sectionClassName}>
        <div className="space-y-6">
          <div className="rounded-lg border-2 border-cpaGold p-6">
            <h3 className="text-[20px] font-extrabold text-gray-950">
              Send Test Email
            </h3>
            <p className="mt-2 text-[15px] leading-6 text-gray-500">
              Send a test email to yourself or a colleague to review before
              sending to all recipients. You will confirm the exact recipients
              and sender before anything is sent.
            </p>
            <label className="mt-5 block">
              <span className={fieldLabelClassName}>Send test email to</span>
              <input
                value={testEmailInput}
                onChange={(event) => {
                  setTestEmailInput(event.target.value);
                  setTestEmailError("");
                }}
                className={`${inputClassName} mt-3`}
                placeholder="Enter email address"
                disabled={isBusy}
              />
            </label>
            {testEmailError ? (
              <p className="mt-2 text-sm font-semibold text-red-600">
                {testEmailError}
              </p>
            ) : null}
            <p className="mt-2 text-[13px] text-gray-400">
              Separate multiple emails with a comma
            </p>
            <button
              type="button"
              className={`${primaryButtonClassName} mt-5`}
              onClick={handleSendTestEmail}
              disabled={!canRunSavedBackendAction || !hasSendReviewAction || isBusy}
            >
              Send Test Email
            </button>
            {!canRunSavedBackendAction || !hasSendReviewAction ? (
              <p className="mt-3 text-sm text-gray-500">
                Save the draft first, then send the test email from the saved
                email.
              </p>
            ) : null}
          </div>
          <div className="rounded-lg border-2 border-emerald-600 p-6">
            <h3 className="text-[20px] font-extrabold text-gray-950">
              Send Email
            </h3>
            <p className="mt-2 text-[15px] leading-6 text-gray-500">
              {intendedRecipientCount === null
                ? "Save this email, then send it to the latest resolved audience."
                : `Save this email, then send it to all ${intendedRecipientCountLabel} selected recipients.`}
            </p>
            <button
              type="button"
              className={`${successButtonClassName} mt-5`}
              onClick={sendNow}
              disabled={!hasSendNowAction || isBusy || isSentRecord}
            >
              Send Email
            </button>
            {!hasSendNowAction ? (
              <p className="mt-3 text-sm text-gray-500">
                Sending is not available for this email.
              </p>
            ) : null}
          </div>
        </div>
      </section>
      <ConfirmDialog
        open={!!pendingTestSend}
        variant="confirm"
        title="Send this test email?"
        message={
          pendingTestSend ? (
            <>
              <span className="block">
                <span className="font-bold text-gray-900">To:</span>{" "}
                {pendingTestSend.preview.normalizedRecipientEmails.join(", ")}
              </span>
              <span className="mt-2 block">
                <span className="font-bold text-gray-900">From:</span>{" "}
                {pendingTestSend.preview.fromEmail}
              </span>
              <span className="mt-2 block">
                <span className="font-bold text-gray-900">Reply-To:</span>{" "}
                {pendingTestSend.preview.replyToEmail || "Not set"}
              </span>
              <span className="mt-3 block">
                This sends only to the addresses above, not to the campaign
                audience.
              </span>
              {testEmailError ? (
                <span className="mt-3 block font-semibold text-red-600">
                  {testEmailError}
                </span>
              ) : null}
            </>
          ) : undefined
        }
        confirmLabel="Send Test Email"
        cancelLabel="Go Back"
        confirmColor="gold"
        loading={isBusy}
        onConfirm={confirmSendTestEmail}
        onClose={() => {
          if (!isBusy) setPendingTestSend(null);
        }}
      />
    </div>
  );
};

export default StepFourReview;
